import {
  Box,
  IconButton,
  MinusCircleIcon,
  PlusCircleIcon,
  TextField,
  Typography,
} from '@customink/pigment-react';
import type { SvgIconProps } from '@customink/pigment-react/lib/components/SvgIcon';
import { BULK_SPLIT_SHIP } from '@customink/project-service-react-components';
import { type ElementRef, useRef } from 'react';

import { useProjectTotalQuantity } from '@/hooks/Project';
import { spacing12, spacing48 } from '@/utils/designTokens';

const { MIN_ADDRESS_COUNT, SHIPPING_FEE } = BULK_SPLIT_SHIP;

type ColorVariant = SvgIconProps['color'];

type ShipToMultipleProps = {
  addressCount?: number;
  onChangeAddressCount?: (newAddressCount: number) => void;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const NOOP = () => {};

export default function ShipToMultiple({
  addressCount = 2,
  onChangeAddressCount = NOOP,
}: ShipToMultipleProps) {
  const maxAddressCount = useProjectTotalQuantity();
  const inputRef = useRef<ElementRef<'input'>>();

  const changeHandler = (count: number) => {
    if (!inputRef.current) return;

    let newCount = count;
    if (count < MIN_ADDRESS_COUNT) newCount = MIN_ADDRESS_COUNT;
    if (count > maxAddressCount) newCount = maxAddressCount;
    if (isNaN(newCount)) newCount = MIN_ADDRESS_COUNT;

    onChangeAddressCount(newCount);
    inputRef.current.value = newCount.toString();
  };
  const onIncrement = () => changeHandler(addressCount + 1);
  const onDecrement = () => changeHandler(addressCount - 1);

  const minusCircleIconColor: ColorVariant =
    addressCount <= MIN_ADDRESS_COUNT ? 'disabled' : 'blue';
  const plusCircleIconColor: ColorVariant =
    addressCount >= maxAddressCount ? 'disabled' : 'blue';

  return (
    <Box>
      <Typography data-testid="ship-to-multiple">
        Number of addresses
      </Typography>
      <Box display="inline-block">
        <IconButton
          aria-label="minusButton"
          data-testid="minus-button"
          disabled={addressCount === MIN_ADDRESS_COUNT}
          onClick={onDecrement}
          size="large"
        >
          <MinusCircleIcon color={minusCircleIconColor} fontSize="inherit" />
        </IconButton>
        <TextField
          defaultValue={addressCount}
          inputProps={{
            style: {
              width: spacing48,
              textAlign: 'center',
              height: spacing12,
            },
            'aria-label': 'quantity',
            'data-testid': 'address-count-input',
          }}
          inputRef={inputRef}
          onBlur={(e) => changeHandler(parseInt(e.target.value, 10))}
          removeHelperTextSpace
          type="tel"
        />
        <IconButton
          aria-label="plusButton"
          data-testid="plus-button"
          disabled={addressCount === maxAddressCount}
          onClick={onIncrement}
          size="large"
        >
          <PlusCircleIcon color={plusCircleIconColor} fontSize="inherit" />
        </IconButton>
      </Box>
      <Typography>{`$${SHIPPING_FEE}${'\u00A0'}per${'\u00A0'}address`}</Typography>
    </Box>
  );
}
